import { useI18n } from "../i18n/useI18n";
import type { UiKey } from "../i18n/ui";
import { toggleFeature } from "../lib/workloadState";
import type { FeatureFlag, Workload } from "../lib/types";

const FEATURE_ORDER: FeatureFlag[] = [
  "chat",
  "storage",
  "search",
  "ai",
  "realtime",
  "files",
];

const FEATURE_LABELS: Record<FeatureFlag, UiKey> = {
  chat: "planning.feat.chat",
  storage: "planning.feat.storage",
  search: "planning.feat.search",
  ai: "planning.feat.ai",
  realtime: "planning.feat.realtime",
  files: "planning.feat.files",
};

type Props = {
  workload: Workload;
  onChange: (w: Workload) => void;
};

export function PlanningForm({ workload, onChange }: Props) {
  const { t } = useI18n();
  const hasAi = workload.enabledFeatures.includes("ai");

  function patch(partial: Partial<Workload>) {
    onChange({ ...workload, ...partial });
  }

  function setFeature(flag: FeatureFlag, on: boolean) {
    const nextFeatures = toggleFeature(workload.enabledFeatures, flag, on);
    let storageKb = workload.storageKbPerUser;
    if (flag === "files") {
      storageKb = on ? 512 : 48;
    }
    let aiShare = workload.aiRequestShare;
    let pt = workload.avgPromptTokens;
    let ct = workload.avgCompletionTokens;
    if (flag === "ai" && !on) {
      aiShare = 0;
      pt = 0;
      ct = 0;
    }
    if (flag === "ai" && on && pt === 0 && ct === 0) {
      pt = 180;
      ct = 320;
      aiShare = aiShare || 0.35;
    }
    onChange({
      ...workload,
      enabledFeatures: nextFeatures,
      storageKbPerUser: storageKb,
      aiRequestShare: nextFeatures.includes("ai") ? aiShare : 0,
      avgPromptTokens: nextFeatures.includes("ai") ? pt : 0,
      avgCompletionTokens: nextFeatures.includes("ai") ? ct : 0,
    });
  }

  return (
    <div className="planning">
      <h2>{t("planning.title")}</h2>
      <p className="hint">{t("planning.hint")}</p>

      <div className="row">
        <label>
          <span>{t("planning.usersDaily")}</span>
          <input
            type="number"
            min={1}
            step={1}
            value={workload.usersDaily}
            onChange={(e) =>
              patch({
                usersDaily: Math.max(1, Math.round(Number(e.target.value) || 1)),
              })
            }
          />
        </label>
        <label>
          <span>{t("planning.requestsPerUser")}</span>
          <input
            type="number"
            min={1}
            step={1}
            value={workload.requestsPerUser}
            onChange={(e) =>
              patch({
                requestsPerUser: Math.max(1, Math.round(Number(e.target.value) || 1)),
              })
            }
          />
        </label>
      </div>

      <fieldset className="features">
        <legend>{t("planning.features")}</legend>
        <div className="checks">
          {FEATURE_ORDER.map((flag) => (
            <label key={flag} className="check">
              <input
                type="checkbox"
                checked={workload.enabledFeatures.includes(flag)}
                onChange={(e) => setFeature(flag, e.target.checked)}
              />
              {t(FEATURE_LABELS[flag])}
            </label>
          ))}
        </div>
      </fieldset>

      {hasAi && (
        <div className="ai-block">
          <p className="ai-title">{t("planning.aiSection")}</p>
          <div className="row">
            <label>
              <span>{t("planning.promptTokens")}</span>
              <input
                type="number"
                min={0}
                step={10}
                value={workload.avgPromptTokens}
                onChange={(e) =>
                  patch({
                    avgPromptTokens: Math.max(
                      0,
                      Math.round(Number(e.target.value) || 0),
                    ),
                  })
                }
              />
            </label>
            <label>
              <span>{t("planning.completionTokens")}</span>
              <input
                type="number"
                min={0}
                step={10}
                value={workload.avgCompletionTokens}
                onChange={(e) =>
                  patch({
                    avgCompletionTokens: Math.max(
                      0,
                      Math.round(Number(e.target.value) || 0),
                    ),
                  })
                }
              />
            </label>
          </div>
          <label className="share">
            <span>
              {t("planning.aiShare")}: {Math.round(workload.aiRequestShare * 100)}%
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round(workload.aiRequestShare * 100)}
              onChange={(e) => patch({ aiRequestShare: Number(e.target.value) / 100 })}
            />
          </label>
        </div>
      )}

      <style>{`
        .planning h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .hint {
          margin: 0 0 1rem;
          font-size: 0.85rem;
          color: var(--muted);
        }
        .row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        @media (max-width: 520px) {
          .row {
            grid-template-columns: 1fr;
          }
        }
        .row label,
        .share {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          font-size: 0.85rem;
        }
        .row label span,
        .share span {
          color: var(--muted);
        }
        .row input[type="number"] {
          padding: 0.5rem 0.65rem;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--bg);
          color: var(--text);
          font-family: var(--mono);
          font-size: 0.85rem;
        }
        .features {
          border: 1px solid var(--border);
          border-radius: 8px;
          padding: 0.75rem 1rem 1rem;
          margin: 0 0 1rem;
        }
        .features legend {
          padding: 0 0.35rem;
          font-size: 0.85rem;
          color: var(--text);
        }
        .checks {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem 1rem;
        }
        .check {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.85rem;
          cursor: pointer;
        }
        .ai-block {
          padding-top: 0.5rem;
          border-top: 1px solid var(--border);
        }
        .ai-title {
          margin: 0 0 0.65rem;
          font-size: 0.85rem;
          font-weight: 600;
        }
        .share input[type="range"] {
          width: 100%;
          accent-color: var(--accent);
          min-height: 44px;
          padding-block: 0.45rem;
        }
        @media (max-width: 720px) {
          .row input[type="number"] {
            min-height: 48px;
          }
          .check {
            min-height: 44px;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
}
